import React from "react";
import { observer } from "mobx-react";
import { observable, computed } from "mobx";
import { FieldRenderProps } from "react-final-form";

import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { FormLabel, IconButton, Typography } from "@material-ui/core";

import DropZone, { ExternalDropZoneProps } from "components/DropZone";

import withFieldControl from "../FieldControl";

import { readImageContent } from "utils/images";
import { readCSVContent } from "utils/csv";

import {
  Container,
  CsvContainer,
  CsvPreview,
  IconControlsContainer,
  ImageContainer,
} from "./styles";
import SvgI24Csv from "assets/icons/I24Csv";
import ManualBadgeStore from "stores/ManualBadgeStore";

type Props = {
  className?: string;
  badgeId?: number;
  invalidFileTypeMessage?: string;
  updateStatus?: (status: boolean) => void;
  updateShowUpload?: (status: boolean) => void;
  updateShowSuccess?: (status: boolean) => void;
  updateShowUnSuccess?: (status: boolean) => void;
  disableSaveButton?: (status: boolean) => void;
  withoutDeleteAction?: boolean;
  emailsFailWithReasons?: (errors: any[]) => void;
  successfullUploads?: (value: number | undefined) => void;
  setIsLoading?: (isLoading: boolean) => void;
  updateEmails?: string[];
} & FieldRenderProps<any> &
  ExternalDropZoneProps;

@observer
class UploadPreviewFieldControl extends React.Component<Props> {
  dropZoneRef = React.createRef<DropZone>();
  reader = new FileReader();

  @observable imagePreview: any = "";
  @observable filePreview: any = "";
  @observable fileName: string = "";

  @computed get image() {
    const { input } = this.props;
    return this.imagePreview || input.value;
  }

  @computed get isPdf() {
    return this.props.acceptedFiles?.split(",").includes(".pdf");
  }

  @computed get file() {
    const { input } = this.props;
    return this.filePreview || input.value;
  }

  @computed get isCsv() {
    return this.props.acceptedFiles?.split(",").includes(".csv");
  }

  get dropzoneElement() {
    return this.dropZoneRef?.current;
  }

  emailsVerification = async (id: number, emailsData: string[]) =>
    await ManualBadgeStore.checkUsersEmails(id, emailsData);

  extractEmails = (arr: any[]): any => {
    return arr
      .map(obj => obj.email)
      .filter(email => email !== null && email !== undefined);
  };

  addedfile = async (file: File) => {
    const {
      input,
      badgeId,
      updateStatus,
      updateShowUpload,
      updateShowSuccess,
      disableSaveButton,
      updateShowUnSuccess,
      emailsFailWithReasons,
      successfullUploads,
      setIsLoading,
      updateEmails,
    } = this.props;
    const event: any = {
      target: { value: file },
    };
    input.onChange(event);
    if (updateShowUpload) updateShowUpload(true);
    if (setIsLoading) setIsLoading(true);

    let resultFile;
    if (this.isCsv) {
      resultFile = await readCSVContent(file);
      this.filePreview = file;
      this.fileName = file.name;
      if (resultFile) {
        const emailsData = this.extractEmails(resultFile);
        try {
          const verificationResult = await this.emailsVerification(
            badgeId,
            emailsData,
          );
          if (successfullUploads)
            successfullUploads(verificationResult?.usersSuccessfulyLoaded);
          if (
            verificationResult &&
            verificationResult.emailUnsuccessfuls &&
            verificationResult.emailUnsuccessfuls.length > 0
          ) {
            if (emailsFailWithReasons)
              emailsFailWithReasons(verificationResult.emailUnsuccessfuls);
            if (setIsLoading) setIsLoading(false);
            if (updateShowUnSuccess) updateShowUnSuccess(true);
            if (updateShowSuccess) updateShowSuccess(false);
            if (disableSaveButton) disableSaveButton(true);
          } else {
            if (updateShowSuccess) updateShowSuccess(true);
            if (updateShowUnSuccess) updateShowUnSuccess(false);
            if (disableSaveButton) disableSaveButton(false);
            if (updateEmails) updateEmails(emailsData);
            if (setIsLoading) setIsLoading(false);
          }
          if (updateStatus) updateStatus(true);
        } catch (error) {
          console.error("Error during emails verification:", error);
          if (updateShowUnSuccess) updateShowUnSuccess(true);
          if (updateShowSuccess) updateShowSuccess(false);
          if (disableSaveButton) disableSaveButton(true);
          if (updateStatus) updateStatus(false);
        }
      } else {
        if (setIsLoading) setIsLoading(false);
        if (updateStatus) updateStatus(false);
      }
    } else {
      const resultImage = await readImageContent(this.reader, file);
      if (resultImage.success) {
        this.imagePreview = resultImage.result;
      }
    }
  };

  removedFile = () => {
    const {
      input,
      updateStatus,
      updateShowUpload,
      disableSaveButton,
    } = this.props;

    const event: any = {
      target: { value: "" },
    };
    input.onChange(event);

    if (updateStatus) updateStatus(false);
    if (updateShowUpload) updateShowUpload(false);
    if (disableSaveButton) disableSaveButton(true);
    this.filePreview = "";
    this.imagePreview = "";
    this.fileName = "";
  };

  openFileSelector = () => {
    if (this.dropzoneElement) {
      this.dropzoneElement.dropzone.element.click();
    }
  };

  render() {
    const {
      meta,
      required,
      title,
      className,
      withoutDeleteAction,
      ...props
    } = this.props;

    const error = (meta.touched || meta.dirty) && meta.error;
    return (
      <Container className={className}>
        {title && (
          <FormLabel required={required} error={error}>
            {title}
          </FormLabel>
        )}
        {this.isCsv ? (
          <CsvContainer file={this.file}>
            <DropZone
              ref={this.dropZoneRef}
              componentConfig={{ postUrl: "no-url" }}
              eventHandlers={{
                addedfile: this.addedfile,
                removedfile: this.removedFile,
              }}
              error={error}
              autoUpload={false}
              maxFiles={1}
              required={required}
              fitWidth
              hideProgressBar
              {...props}
            />
            {this.fileName && (
              <CsvPreview>
                <SvgI24Csv />
                <Typography variant="body2">{this.fileName}</Typography>
              </CsvPreview>
            )}
            <IconControlsContainer>
              {!withoutDeleteAction && this.file && (
                <IconButton onClick={this.removedFile} size="small">
                  <DeleteOutlineIcon />
                </IconButton>
              )}
              <IconButton onClick={this.openFileSelector} size="small">
                <EditIcon />
              </IconButton>
            </IconControlsContainer>
          </CsvContainer>
        ) : (
          <ImageContainer image={this.image} hasError={error} title={title}>
            <DropZone
              ref={this.dropZoneRef}
              componentConfig={{ postUrl: "no-url" }}
              eventHandlers={{
                addedfile: this.addedfile,
                removedfile: this.removedFile,
              }}
              error={error}
              autoUpload={false}
              maxFiles={1}
              required={required}
              fitWidth
              hideProgressBar
              {...props}
            />
            <IconControlsContainer>
              {!withoutDeleteAction && this.image && (
                <IconButton onClick={this.removedFile} size="small">
                  <DeleteOutlineIcon />
                </IconButton>
              )}
              <IconButton onClick={this.openFileSelector} size="small">
                <EditIcon />
              </IconButton>
            </IconControlsContainer>
          </ImageContainer>
        )}
      </Container>
    );
  }
}

export default withFieldControl(UploadPreviewFieldControl);
